import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

/**
 * Password Reset Page - Supabase Recovery Flow ONLY
 * 
 * This page ONLY supports Supabase's password recovery flow using access_token from hash.
 * NO PKCE, NO code exchange, NO OAuth callbacks.
 * 
 * Flow:
 * 1. Wait for window.location.hash
 * 2. Extract access_token, refresh_token, type=recovery
 * 3. Call supabase.auth.setSession({ access_token, refresh_token })
 * 4. Show password form
 * 5. Call supabase.auth.updateUser({ password })
 * 6. Redirect to login
 */
export default function PasswordReset() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [sessionValid, setSessionValid] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    let retryCount = 0;
    const maxRetries = 5; // Retry 5 times over ~1 second
    const retryIntervalMs = 200;
    let retryTimeout = null;
    let hashChangeHandler = null;

    /**
     * Extract recovery tokens from window.location.hash
     * ONLY extracts: access_token, refresh_token, type
     * IGNORES: code, code_verifier, and any PKCE-related params
     */
    const extractRecoveryTokens = () => {
      const hash = window.location?.hash;
      console.log('[RESET] ========== RECOVERY TOKEN EXTRACTION ==========');
      console.log('[RESET] Full URL:', window.location?.href);
      console.log('[RESET] Hash:', hash);

      if (!hash || hash?.length <= 1) {
        return null;
      }

      // Remove # and parse
      const hashParams = hash?.substring(1);
      const params = new URLSearchParams(hashParams);

      // ONLY extract recovery flow tokens
      const access_token = params?.get('access_token');
      const refresh_token = params?.get('refresh_token');
      const type = params?.get('type');
      const error = params?.get('error');
      const error_description = params?.get('error_description');

      const extracted = {
        access_token,
        refresh_token,
        type,
        error,
        error_description
      };

      console.log('[RESET] Extracted params:', {
        has_access_token: !!access_token,
        has_refresh_token: !!refresh_token,
        type: type,
        has_error: !!error
      });

      return extracted;
    };

    /**
     * Set session from recovery tokens
     * This is the ONLY way to establish a recovery session
     */
    const setRecoverySession = async (access_token, refresh_token) => {
      try {
        console.log('[RESET] Setting recovery session from access_token...');
        
        const { data: { session }, error: sessionError } = await supabase?.auth?.setSession({
          access_token: access_token,
          refresh_token: refresh_token || access_token // Use access_token as fallback if refresh_token missing
        });

        if (sessionError) {
          console.error('[RESET] Session error:', sessionError);
          if (mounted) {
            // Detect specific error types and show appropriate messages
            const errorMessage = sessionError?.message || '';
            const errorCode = sessionError?.status || '';
            
            let specificError = '';
            if (errorMessage?.includes('already been used') || 
                errorMessage?.includes('already used') ||
                errorMessage?.includes('token_already_used') ||
                errorCode === 400) {
              specificError = 'This password reset link has already been used. Password reset links can only be used once. Please request a new password reset link.';
            } else if (errorMessage?.includes('expired') || 
                       errorMessage?.includes('expired_token') ||
                       errorMessage?.includes('token_expired')) {
              specificError = 'This password reset link has expired. Links are valid for 1 hour. Please request a new password reset link.';
            } else if (errorMessage?.includes('invalid') || 
                       errorMessage?.includes('invalid_token')) {
              specificError = 'This password reset link is invalid. Please request a new password reset link.';
            } else {
              specificError = 'This password reset link is invalid or has expired. Please request a new password reset link.';
            }
            
            setError(specificError);
            setVerifying(false);
          }
          return false;
        }

        if (!session) {
          console.error('[RESET] No session created');
          if (mounted) {
            setError('Failed to create recovery session. Please request a new password reset.');
            setVerifying(false);
          }
          return false;
        }

        // Verify this is a recovery session
        if (session?.user) {
          console.log('[RESET] ✅ Recovery session established successfully');
          if (mounted) {
            setSessionValid(true);
            setMessage('✅ Reset link verified! Please enter your new password below.');
            setError('');
            setVerifying(false);
          }
          return true;
        }

        return false;
      } catch (err) {
        console.error('[RESET] Session setup error:', err);
        if (mounted) {
          setError(`Failed to set session: ${err?.message || 'Please try again.'}`);
          setVerifying(false);
        }
        return false;
      }
    };

    /**
     * Main verification logic
     * ONLY processes recovery flow with access_token
     */
    const verifyRecoveryLink = async () => {
      const tokens = extractRecoveryTokens();

      // Check for errors in URL
      if (tokens?.error) {
        console.error('[RESET] Error in URL:', tokens?.error);
        if (mounted) {
          let errorMsg = '';
          const errorCode = tokens?.error;
          const errorDesc = tokens?.error_description || '';
          
          // Detect specific error types
          if (errorCode === 'expired_token' || errorDesc?.includes('expired')) {
            errorMsg = 'This password reset link has expired. Links are valid for 1 hour. Please request a new password reset link.';
          } else if (errorCode === 'invalid_token' || errorDesc?.includes('invalid')) {
            errorMsg = 'This password reset link is invalid. Please request a new password reset link.';
          } else if (errorDesc?.includes('already been used') || errorDesc?.includes('already used')) {
            errorMsg = 'This password reset link has already been used. Password reset links can only be used once. Please request a new password reset link.';
          } else {
            errorMsg = 'This password reset link is invalid or has expired. Please request a new password reset link.';
          }
          
          setError(errorMsg);
          setVerifying(false);
        }
        return false;
      }

      // Must have access_token and type=recovery
      if (!tokens?.access_token) {
        return false; // Will retry
      }

      if (tokens?.type !== 'recovery') {
        console.warn('[RESET] Token type is not "recovery":', tokens?.type);
        if (mounted) {
          setError('Invalid reset link type. Please use the link from your password reset email.');
          setVerifying(false);
        }
        return false;
      }

      // Set session from recovery tokens
      return await setRecoverySession(tokens?.access_token, tokens?.refresh_token);
    };

    /**
     * Check if Supabase has already auto-detected a valid recovery session
     * This handles cases where detectSessionInUrl: true has already set the session
     */
    const checkExistingSession = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
        
        if (sessionError) {
          console.log('[RESET] Session check error:', sessionError);
          return false;
        }

        if (session?.user) {
          console.log('[RESET] ✅ Found existing valid session (auto-detected by Supabase)');
          if (mounted) {
            setSessionValid(true);
            setMessage('✅ Reset link verified! Please enter your new password below.');
            setError('');
            setVerifying(false);
          }
          return true;
        }

        return false;
      } catch (err) {
        console.log('[RESET] Session check exception:', err);
        return false;
      }
    };

    /**
     * Initial verification with retry logic
     * Retries for up to 1 second to handle browser hash loading delays
     */
    const attemptVerification = async () => {
      const success = await verifyRecoveryLink();
      
      if (success) {
        return; // Success, stop retrying
      }

      // If no tokens found yet, retry
      if (retryCount < maxRetries) {
        retryCount++;
        console.log(`[RESET] Retrying token detection (${retryCount}/${maxRetries})...`);
        retryTimeout = setTimeout(attemptVerification, retryIntervalMs);
      } else {
        // All retries exhausted - check if Supabase auto-detected the session
        console.log('[RESET] No recovery tokens found in hash after all retries. Checking for auto-detected session...');
        const hasSession = await checkExistingSession();
        
        if (!hasSession && mounted && !error) {
          // Only show error if there's no valid session
          console.error('[RESET] No recovery tokens found and no valid session detected.');
          setError('No reset token found in the URL. Please make sure you clicked the link directly from the password reset email. If you already used this link, please request a new password reset.');
          setVerifying(false);
        }
      }
    };

    // Listen for hash changes (in case Supabase redirects after page load)
    hashChangeHandler = () => {
      console.log('[RESET] Hash changed, re-verifying...');
      if (retryTimeout) clearTimeout(retryTimeout);
      retryCount = 0; // Reset retry count
      attemptVerification();
    };

    window.addEventListener('hashchange', hashChangeHandler);

    // First, check if Supabase has already auto-detected a session
    // This handles cases where the session was set before our code runs
    checkExistingSession()?.then((hasSession) => {
      if (!hasSession) {
        // If no session found, start verification with retry logic
        attemptVerification();
      }
    });

    return () => {
      mounted = false;
      if (retryTimeout) clearTimeout(retryTimeout);
      window.removeEventListener('hashchange', hashChangeHandler);
    };
  }, []);

  /**
   * Handle password reset submission
   * ONLY uses supabase.auth.updateUser() - requires active recovery session
   */
  const handlePasswordReset = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    // Validation
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (password?.length < 6) {
      setError('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    try {
      console.log('[RESET] Attempting to reset password...');

      // Verify session is still valid
      const { data: { session }, error: sessionCheckError } = await supabase?.auth?.getSession();
      
      if (sessionCheckError || !session) {
        console.error('[RESET] Session check failed before update:', sessionCheckError);
        let errorMsg = sessionCheckError?.message || '';
        if (errorMsg?.includes('already been used') || errorMsg?.includes('already used')) {
          setError('This password reset link has already been used. Password reset links can only be used once. Please request a new password reset link.');
        } else {
          setError('Your recovery session has expired. Please request a new password reset link.');
        }
        setLoading(false);
        return;
      }

      // Update password using Supabase's updateUser (requires active recovery session)
      const { data, error: updateError } = await supabase?.auth?.updateUser({
        password: password
      });

      if (updateError) {
        console.error('[RESET] Password update error:', updateError);
        let errorMsg = updateError?.message || '';
        if (errorMsg?.includes('already been used') || errorMsg?.includes('already used') || errorMsg?.includes('token_already_used')) {
          setError('This password reset link has already been used. Password reset links can only be used once. Please request a new password reset link.');
        } else if (errorMsg?.includes('expired') || errorMsg?.includes('expired_token')) {
          setError('Your recovery session has expired. Please request a new password reset link.');
        } else {
          setError(`Failed to update password: ${errorMsg || 'Please try again'}`);
        }
        setLoading(false);
        return;
      }

      console.log('[RESET] ✅ Password reset successfully');
      setMessage('✅ Password reset successfully! Redirecting to login...');

      // Sign out to clear the recovery session and ensure a fresh login
      await supabase?.auth?.signOut();

      // Redirect to login page after 2 seconds
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 2000);

    } catch (err) {
      console.error('[RESET] Unexpected error during password reset:', err);
      setError(`An unexpected error occurred: ${err?.message || 'Please try again'}`);
      setLoading(false);
    }
  };

  // Show loading state while verifying
  if (verifying) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600">Verifying reset link...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Reset Your Password
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          {sessionValid ? 'Enter your new password below' : 'There was a problem with your reset link'}
        </p>
      </div>
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {/* Error Messages */}
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
              <p className="font-medium">⚠️ Reset Link Issue</p>
              <p className="text-sm mt-1">{error}</p>
            </div>
          )}

          {/* Success Messages */}
          {message && (
            <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded relative">
              {message}
            </div>
          )}

          {/* Password Reset Form - Only show if session is valid */}
          {sessionValid ? (
            <form onSubmit={handlePasswordReset} className="space-y-6">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  New Password
                </label>
                <div className="mt-1">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e?.target?.value)}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter your new password"
                    minLength="6"
                    disabled={loading}
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">Must be at least 6 characters</p>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                  Confirm New Password
                </label>
                <div className="mt-1">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e?.target?.value)}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Confirm your new password"
                    minLength="6"
                    disabled={loading}
                  />
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                      Resetting password...
                    </>
                  ) : (
                    'Reset Password'
                  )}
                </button>
              </div>
            </form>
          ) : (
            /* Invalid Session - Show instructions */
            (<div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm font-medium text-blue-900 mb-2">📋 To reset your password:</p>
                <ol className="list-decimal list-inside text-sm text-blue-800 space-y-2">
                  <li>Go back to the login page</li>
                  <li>Click "Forgot Password?" in the Login tab</li>
                  <li>Enter your email address</li>
                  <li>Check your email (including spam folder)</li>
                  <li>Click the link in the email</li>
                  <li>✅ Works in any browser - no restrictions!</li>
                </ol>
              </div>
              <button
                onClick={async () => {
                  // Sign out before redirecting to ensure we go to login page, not dashboard
                  await supabase?.auth?.signOut();
                  navigate('/', { replace: true });
                }}
                className="w-full flex justify-center py-2 px-4 border border-blue-600 text-sm font-medium rounded-md text-blue-600 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                ← Back to Login
              </button>
            </div>)
          )}

          {/* Back to Login Link - only show if form is visible */}
          {sessionValid && (
            <div className="mt-6 text-center">
              <button
                onClick={async () => {
                  // Sign out before redirecting to ensure we go to login page, not dashboard
                  await supabase?.auth?.signOut();
                  navigate('/', { replace: true });
                }}
                className="text-sm text-blue-600 hover:text-blue-500"
              >
                ← Back to login
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
